import React from 'react';
import styled, { css } from 'styled-components';
import { Product } from '@inter-ikea-storageone/range';
import ProductImage from 'components/ProductImage/ProductImage';
import { getMPPImage, getTranslatedTitle } from 'products';
import useOnInfoClicked from 'components/ProductSidebar/MobileMenu/utils/useOnInfoClicked';
import InfoButton from 'components/ProductSidebar/InfoButton/InfoButton';
import useIsRtl from 'hooks/useIsRtl';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useIntl } from 'react-intl';
import { getProductDimensionsForNarration } from 'utils/measurementUtils';
import { ProductCardImage } from 'rangeConfig/@types';

const itemSize = css`
  width: 4.5rem;
  margin: 0.3rem 0.25rem;
`;

const ProductCardWrapper = styled.div<{ isRtl: boolean }>`
  position: relative;
  overflow: visible;
  ${itemSize}
`;

const InfoButtonWrapper = styled.div``;

type ProductCardProps = {
  productCluster: Product[];
  onSelect: () => void;
  selected: boolean;
  mainImage?: ProductCardImage;
  showInfoButton?: boolean;
  mobileCardText: string | undefined;
};

const ProductCard = ({
  productCluster,
  onSelect,
  selected,
  mainImage,
  showInfoButton,
  mobileCardText,
  ...props
}: ProductCardProps) => {
  const product = productCluster[0];
  const mainCardImage = (mainImage && mainImage(product)) || getMPPImage(product);
  const infoClick = useOnInfoClicked(product, productCluster);
  const isRtl = useIsRtl();
  const { formatMessage } = useIntl();

  const onInfoClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    infoClick(event);
  };

  const positionRight = isRtl ? 'auto' : '-4px';
  const positionLeft = isRtl ? '-10px' : 'auto';

  return (
    <>
      <ProductCardWrapper isRtl={isRtl} {...props} onClick={onSelect} data-selected={selected}>
        {showInfoButton && selected && (
          <InfoButtonWrapper
            style={{
              position: 'absolute',
              left: positionLeft,
              right: positionRight,
              top: `-10px`
            }}
          >
            <InfoButton
              productTitle={product.name}
              overflowContainer
              onClick={onInfoClick}
              dataTestId="info-btn"
            />
          </InfoButtonWrapper>
        )}
        {mainCardImage && (
          <ProductImage
            sizes="5rem"
            alt={product.name}
            image={mainCardImage}
            style={{ border: selected ? '1px solid black' : '1px solid transparent' }}
          />
        )}
        {mobileCardText && (
          <Body
            translate="no"
            type={ThemeFontStyleTypeEnum.bodySmall}
            pt="xxs"
            textAlign={'center'}
            style={{ wordBreak: 'break-word' }}
            aria-label={
              product.communicatedDimensions
                ? getProductDimensionsForNarration(formatMessage, product)
                : ''
            }
          >
            {getTranslatedTitle(mobileCardText, product, formatMessage)}
          </Body>
        )}
      </ProductCardWrapper>
    </>
  );
};

export default ProductCard;
